import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowPathIcon, 
  XCircleIcon, 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusCircleIcon
} from '@heroicons/react/24/outline';
import toast from '../utils/toast';
import api from '../services/api';
import ExportButtonsWithAll from './ExportButtonsWithAll';
import TaskCreateModal from './TaskCreateModal';

// Status color mapping for trademark portfolio
const STATUS_COLORS = {
  'Abandoned': '#46c37b', 
  'Accepted & Advertised': '#70b9eb',
  'Advertised bef acc': '#5c90d2',
  'Exam Report Issued': '#f3b760',
  'Cancelled': '#d26a5c',
  'Formalities Chk Fail': '#d26a5c',
  'Formalities Chk Pass': '#46c37b',
  'Marked for Exam': '#f3b760',
  'New Application': '#5c90d2',
  'Objected': '#d26a5c',
  'Opposed': '#d26a5c',
  'Refused': '#f3b760',
  'Registered': '#70b9eb',
  'Removed': '#d26a5c',
  'Send Back to EDP': '#d26a5c',
  'Send to PRAS': '#46c37b',
  'Send To Vienna Codification': '#d26a5c',
  'Withdrawn': '#5c90d2',
  'Withdraw Found': '#5c90d2',
  'Rectification Filed': '#f3b760',
  'Accepted/Readvertise': '#70b9eb',
  'Accepted': '#46c37b',
  'ABA/Readvertised': '#70b9eb',
  'Protection Granted': '#46c37b',
  'IR Cancelled': '#d26a5c',
  'Deemed to be Abandoned': '#46c37b',
  'Online New Appl.': '#5c90d2',
  'INVALID (No fee received)': '#d26a5c',
  'Protection Refused': '#f3b760',
  'Provisional Refusal': '#f3b760',
  'Under Division': '#5c90d2',
  'Send to Re-Advertised': '#70b9eb',
  'Acceptance Withdrawn Process': '#d26a5c',
  'Opposition Withdrawn': '#46c37b'
};

// Status options for multi-select
const STATUS_OPTIONS = [
  'Registered',
  'Opposed',
  'Opposed*',
  'Marked for Exam',
  'New Application',
  'Withdrawn',
  'Objected',
  'Abandoned',
  'Refused',
  'Accepted & Advertised',
  'Exam Report Issued',
  'Rectification Filed',
  'Accepted/Readvertise',
  'Advertised bef acc',
  'Formalities Chk Fail',
  'Formalities Chk Pass',
  'Send To Vienna Codification',
  'Accepted',
  'Removed',
  'Send Back to EDP',
  'Cancelled',
  'ABA/Readvertised',
  'Send to PRAS',
  'Protection Granted',
  'IR Cancelled',
  'Deemed to be Abandoned',
  'Online New Appl.',
  'INVALID (No fee received)',
  'Protection Refused',
  'Provisional Refusal',
  'Under Division',
  'Send to Re-Advertised',
  'Acceptance Withdrawn Process',
  'Opposition Withdrawn'
];

// Alerts options for multi-select
const ALERTS_OPTIONS = [
  'Application form not digitised',
  'Renewal of trademark is due. Renewal request in prescribed form is to be filed by the applicant.',
  'Trade mark is likely to be removed due to non filing of Renewal request within prescribed time limit In case of any discripancy',
  'Request for amendment is Pending for processing',
  'Renewal of trademark is overdue. Renewal with Restoration request in prescribed form is to be filed by the applicant.',
  'Ready for Show cause Hearing',
  'Renewal of trademark is overdue. Renewal request with surcharge in prescribed form is to be filed by the applicant.',
  'Awaiting Reply to Examination Report',
  'Reply to Examination Report Not Received application shall be Abandoned',
  'MIS-E (Deficit Application Fee Rs. 500/- ) required',
  'MIS-E (Deficit Application Fee Rs. 1000/- ) required',
  'Awaiting Reply to keep pending notice',
  'Pending Review of Reply to Keep Pending Notice'
];

const TrademarkPortfolio = () => {
  const navigate = useNavigate();
  const [trademarkData, setTrademarkData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [testing, setTesting] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(25);
  const [offset, setOffset] = useState(0);
  
  // Task modal state
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTrademark, setSelectedTrademark] = useState(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    appno: '',
    status: [],
    class: '',
    tmAppliedFor: '',
    buisnessName: '',
    dateOfApp: '',
    alerts: []
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showAlertsDropdown, setShowAlertsDropdown] = useState(false);
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'dsc'

  const fetchTrademarkData = useCallback(async (pageOffset = offset) => {
    setLoading(true);
    setError(null);
    try {
      console.log('📡 Fetching trademark data with pagination and filters...');
      
      // Build URL with filters
      let url = `https://agent3.tmpilot.com/api/global/trademarks?page=${pageOffset+1}&limit=${limit}&sortBy=appno&dir=${sortDirection}`;
      
      // Add filters to URL if they have values
      Object.keys(filters).forEach(key => {
        if (Array.isArray(filters[key]) && filters[key].length > 0) {
          // Handle array fields (like status) 
          filters[key].forEach(value => {
            url += `&filters[${key}][]=${encodeURIComponent(value)}`;
          });
        } else if (filters[key] && typeof filters[key] === 'string' && filters[key].trim() !== '') {
          // Handle string fields
          url += `&filters[${key}]=${encodeURIComponent(filters[key].trim())}`;
        }
      });
      
      console.log('🔗 Request URL:', url);
      
      const response = await api.post('/api-data/get-data', {
        url: url
      });

      if (response.data.success) {
        console.log('✅ Trademark data received:', response.data.data);
        const data = response.data.data;
        
        if (data.response.data && Array.isArray(data.response.data)) {
          setTrademarkData(data.response.data);
          setTotalCount(data.response.total || 0);
          setLastFetched(new Date());
          toast.success(`Loaded ${data.response.data.length} trademark records`);
        } else {
          throw new Error('Invalid data format received from API');
        }
      } else {
        throw new Error(response.data.message || 'Failed to load trademark changes data');
      }
    } catch (error) {
      console.error('Error fetching trademark changes:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load trademark changes data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [limit, offset]);

  // Separate function for fetching data with filters
  const fetchTrademarkDataWithFilters = useCallback(async (pageOffset = offset) => {
    setLoading(true);
    setError(null);
    try {
      console.log('📡 Fetching trademark data with pagination and filters...');
      
      // Build URL with filters
      let url = `https://agent3.tmpilot.com/api/global/trademarks?page=${pageOffset+1}&limit=${limit}&sortBy=appno&dir=${sortDirection}`;
      
      // Add filters to URL if they have values
      Object.keys(filters).forEach(key => {
        if (Array.isArray(filters[key]) && filters[key].length > 0) {
          // Handle array fields (like status)
          filters[key].forEach(value => {
            url += `&filters[${key}][]=${encodeURIComponent(value)}`;
          });
        } else if (filters[key] && typeof filters[key] === 'string' && filters[key].trim() !== '') {
          // Handle string fields
          url += `&filters[${key}]=${encodeURIComponent(filters[key].trim())}`;
        }
      });
      
      console.log('🔗 Request URL:', url);
      
      const response = await api.post('/api-data/get-data', {
        url: url
      });

      if (response.data.success) {
        console.log('✅ Trademark data received:', response.data.data);
        const data = response.data.data;
        
        if (data.response.data && Array.isArray(data.response.data)) {
          setTrademarkData(data.response.data);
          setTotalCount(data.response.total || 0);
          setLastFetched(new Date());
          toast.success(`Loaded ${data.response.data.length} trademark records`);
        } else {
          throw new Error('Invalid data format received from API');
        }
      } else {
        throw new Error(response.data.message || 'Failed to load trademark changes data');
      }
    } catch (error) {
      console.error('Error fetching trademark changes:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load trademark changes data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [limit, offset, filters]);

  const testApiConnection = async () => {
    setTesting(true);
    try {
      console.log('🧪 Testing API connection...');
      const response = await api.post('/api-data/get-data', {
        url: 'https://agent3.tmpilot.com/api/trademarks?token={token}&offset=0&limit=1'
      });

      if (response.data.success) {
        toast.success('API connection test successful!');
        console.log('✅ API test successful:', response.data.data);
      } else {
        toast.error(response.data.message || 'API connection test failed');
      }
    } catch (error) {
      console.error('API test error:', error);
      toast.error(error.response?.data?.message || error.message || 'API connection test failed');
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    fetchTrademarkData();
  }, [fetchTrademarkData]);

  // Handle pagination
  const handlePageChange = (newPage) => {
    const newOffset = (newPage - 1);
    setCurrentPage(newPage);
    setOffset(newOffset);
    
    // Use appropriate fetch function based on whether filters are applied
    if (filtersApplied) {
      fetchTrademarkDataWithFilters(newOffset);
    } else {
      fetchTrademarkData(newOffset);
    }
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle status multi-select changes
  const handleStatusChange = (statusValue, checked) => {
    setFilters(prev => ({
      ...prev,
      status: checked 
        ? [...prev.status, statusValue]
        : prev.status.filter(s => s !== statusValue)
    }));
  };

  // Handle alerts multi-select changes
  const handleAlertsChange = (alertValue, checked) => {
    setFilters(prev => ({
      ...prev,
      alerts: checked 
        ? [...prev.alerts, alertValue]
        : prev.alerts.filter(a => a !== alertValue)
    }));
  };

  // Handle sort direction changes
  const handleSortDirectionChange = (direction) => {
    setSortDirection(direction);
    setCurrentPage(1);
    setOffset(0);
    
    // Make direct API call with new sort direction
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('📡 Fetching trademark data with new sort direction...');
        
        // Build URL with current filters and new sort direction
        let url = `https://agent3.tmpilot.com/api/global/trademarks?page=1&limit=${limit}&sortBy=appno&dir=${direction}`;
        
        // Add filters to URL if they have values
        Object.keys(filters).forEach(key => {
          if (Array.isArray(filters[key]) && filters[key].length > 0) {
            // Handle array fields (like status)
            filters[key].forEach(value => {
              url += `&filters[${key}][]=${encodeURIComponent(value)}`;
            });
          } else if (filters[key] && typeof filters[key] === 'string' && filters[key].trim() !== '') {
            // Handle string fields
            url += `&filters[${key}]=${encodeURIComponent(filters[key].trim())}`;
          }
        });
        
        console.log('🔗 Request URL:', url);
        
        const response = await api.post('/api-data/get-data', {
          url: url
        });

        if (response.data.success) {
          console.log('✅ Trademark data received:', response.data.data);
          const data = response.data.data;
          
          if (data.response.data && Array.isArray(data.response.data)) {
            setTrademarkData(data.response.data);
            setTotalCount(data.response.total || 0);
            setLastFetched(new Date());
            toast.success(`Loaded ${data.response.data.length} trademark records`);
          } else {
            throw new Error('Invalid data format received from API');
          }
        } else {
          throw new Error(response.data.message || 'Failed to load trademark changes data');
        }
      } catch (error) {
        console.error('Error fetching trademark changes:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to load trademark changes data';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  };

  // Apply filters and fetch data from server
  const applyFilters = () => {
    setCurrentPage(1);
    setOffset(0);
    setFiltersApplied(true);
    fetchTrademarkDataWithFilters(0);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      appno: '',
      status: [],
      class: '',
      tmAppliedFor: '',
      buisnessName: '',
      dateOfApp: '',
      alerts: []
    });
    setCurrentPage(1);
    setOffset(0);
    setFiltersApplied(false);
    fetchTrademarkData(0);
  };

  // Handle application number click
  const handleAppNoClick = (appno) => {
    navigate(`/api-data/trademark-portfolio/${appno}`);
  };

  const handleCreateTask = (item) => {
    setSelectedTrademark(item);
    setIsTaskModalOpen(true);
  };

  const getTaskInitialData = () => {
    if (!selectedTrademark) return {};
    
    // Format description with trademark data
    const description = `App No: ${selectedTrademark.appno || 'N/A'}\nTM Applied For: ${selectedTrademark.tmAppliedFor || 'N/A'}\nBusiness Name: ${selectedTrademark.buisnessName || 'N/A'}`;
    
    return {
      name: `Task for ${selectedTrademark.appno || 'Trademark'}`,
      description: description
    };
  };

  const handleTaskCreated = () => {
    // Optionally refresh data or show success message
    toast.success('Task created successfully for trademark');
  };

  const totalPages = Math.ceil(totalCount / limit);

  // Format date to show only year
  const formatYear = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).getFullYear();
    } catch {
      return dateString.split('-')[0] || '-';
    }
  };

  // Get status color for styling
  const getStatusColor = (status) => {
    if (!status) return '#6b7280'; // Default gray color
    return STATUS_COLORS[status] || '#6b7280'; // Return mapped color or default gray
  };

  // Fetch all trademark data for export (without pagination)
  const fetchAllTrademarkData = async () => {
    try {
      console.log('📡 Fetching ALL trademark data for export...');
      
      // Build URL with all filters but no pagination limit
      let url = `https://agent3.tmpilot.com/api/global/trademarks?page=1&limit=${totalCount}&sortBy=appno&dir=${sortDirection}`;
      
      // Add filters to URL if they have values
      Object.keys(filters).forEach(key => {
        if (Array.isArray(filters[key]) && filters[key].length > 0) {
          filters[key].forEach(value => {
            url += `&filters[${key}][]=${encodeURIComponent(value)}`;
          });
        } else if (filters[key] && typeof filters[key] === 'string' && filters[key].trim() !== '') {
          url += `&filters[${key}]=${encodeURIComponent(filters[key].trim())}`;
        }
      });
      
      console.log('🔗 Export Request URL:', url);
      
      const response = await api.post('/api-data/get-data', {
        url: url
      });

      if (response.data.success) {
        console.log(`✅ Fetched ${response.data.data.trademarks?.length || 0} records for export`);
        return response.data.data.trademarks || [];
      }
      
      return [];
    } catch (error) {
      console.error('❌ Error fetching all trademark data:', error);
      throw error;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trademark Portfolio</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and manage trademark application changes from external API
          </p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-2">
          <ExportButtonsWithAll
            data={trademarkData}
            fileName="Trademark_Portfolio"
            columns={[
              { header: 'Application No', key: 'appno', width: 15 },
              { header: 'Proprietor Name', key: 'proprietorName', width: 25 },
              { header: 'Status', key: 'status', width: 20 },
              { header: 'Class', key: 'class', width: 10 },
              { header: 'Type', key: 'type', width: 15 },
              { header: 'Application Date', key: 'applicationDate', width: 15 },
              { header: 'User', key: 'userId', width: 20 }
            ]}
            pdfOptions={{ title: 'Trademark Portfolio', orientation: 'landscape' }}
            fetchAllData={fetchAllTrademarkData}
            totalCount={totalCount}
          />
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Sort by App No:</label>
            <select
              value={sortDirection}
              onChange={(e) => handleSortDirectionChange(e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
          <button
            onClick={() => {
              if (filtersApplied) {
                fetchTrademarkDataWithFilters(offset);
              } else {
                fetchTrademarkData();
              }
            }}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <ArrowPathIcon className="h-5 w-5 mr-2" />
            )}
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </button>
          
          <button
            onClick={testApiConnection}
            disabled={testing}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testing ? (
              <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <CheckCircleIcon className="h-5 w-5 mr-2" />
            )}
            {testing ? 'Testing...' : 'Test API Connection'}
          </button>
        </div>
      </div>

      {/* Stats and Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
          <div className="flex items-center space-x-4 mb-4 lg:mb-0">
            <div className="text-sm text-gray-600">
              Total Records: <span className="font-semibold text-gray-900">{totalCount.toLocaleString()}</span>
            </div>
            <div className="text-sm text-gray-600">
              Showing: <span className="font-semibold text-gray-900">{trademarkData.length}</span> records
            </div>
            <div className="text-sm text-gray-600">
              Page: <span className="font-semibold text-gray-900">{currentPage}</span> of {totalPages}
            </div>
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Application No</label>
                <input
                  type="text"
                  value={filters.appno}
                  onChange={(e) => handleFilterChange('appno', e.target.value)}
                  placeholder="Filter by app no..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <div className="relative">
                  <div 
                    className="border border-gray-300 rounded-md p-2 min-h-[42px] bg-white cursor-pointer"
                    onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  >
                    {filters.status.length === 0 ? (
                      <span className="text-gray-500 text-sm">Select statuses...</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {filters.status.map((status, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {status}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusChange(status, false);
                              }}
                              className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none"
                            >
                              <span className="sr-only">Remove {status}</span>
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                
                {/* Status Options Dropdown */}
                {showStatusDropdown && (
                  <div className="mt-1 max-h-48 overflow-y-auto border border-gray-300 rounded-md bg-white shadow-lg z-10">
                    {STATUS_OPTIONS.map((status) => (
                      <label key={status} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.status.includes(status)}
                          onChange={(e) => handleStatusChange(status, e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{status}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
                <input
                  type="text"
                  value={filters.class}
                  onChange={(e) => handleFilterChange('class', e.target.value)}
                  placeholder="Filter by class..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trademark Applied For</label>
                <input
                  type="text"
                  value={filters.tmAppliedFor}
                  onChange={(e) => handleFilterChange('tmAppliedFor', e.target.value)}
                  placeholder="Filter by trademark..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                <input
                  type="text"
                  value={filters.buisnessName}
                  onChange={(e) => handleFilterChange('buisnessName', e.target.value)}
                  placeholder="Filter by business name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Application</label>
                <input
                  type="text"
                  value={filters.dateOfApp}
                  onChange={(e) => handleFilterChange('dateOfApp', e.target.value)}
                  placeholder="Filter by date (YYYY-MM-DD)..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alerts</label>
                <div className="relative">
                  <div 
                    className="border border-gray-300 rounded-md p-2 min-h-[42px] bg-white cursor-pointer"
                    onClick={() => setShowAlertsDropdown(!showAlertsDropdown)}
                  >
                    {filters.alerts.length === 0 ? (
                      <span className="text-gray-500 text-sm">Select alerts...</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {filters.alerts.map((alert, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
                          >
                            {alert.length > 50 ? alert.substring(0, 50) + '...' : alert}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAlertsChange(alert, false);
                              }}
                              className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-orange-400 hover:bg-orange-200 hover:text-orange-500 focus:outline-none"
                            >
                              <span className="sr-only">Remove {alert}</span>
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                
                {/* Alerts Options Dropdown */}
                {showAlertsDropdown && (
                  <div className="mt-1 max-h-48 overflow-y-auto border border-gray-300 rounded-md bg-white shadow-lg z-10">
                    {ALERTS_OPTIONS.map((alert) => (
                      <label key={alert} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.alerts.includes(alert)}
                          onChange={(e) => handleAlertsChange(alert, e.target.checked)}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{alert}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Filter Action Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={clearFilters}
                className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                <XCircleIcon className="h-4 w-4 mr-2" />
                Clear Filters
              </button>
              <button
                onClick={applyFilters}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                {loading ? 'Applying...' : 'Apply Filters'}
              </button>
            </div>
          </div>
        )}

        {/* Last Fetched Info */}
        {lastFetched && (
          <div className="flex items-center text-sm text-gray-500">
            <InformationCircleIcon className="h-4 w-4 mr-1" />
            Last updated: {lastFetched.toLocaleString()}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <ArrowPathIcon className="h-8 w-8 text-blue-600 mx-auto mb-4 animate-spin" />
            <p className="text-gray-600">Loading trademark portfolio data...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <XCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => fetchTrademarkData()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        ) : trademarkData.length === 0 ? (
          <div className="p-8 text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
            <p className="text-gray-600">
              {trademarkData.length === 0 
                ? 'No trademark portfolio data found. This could mean there are no recent changes or the API returned empty results.'
                : 'No records match your current filters. Try adjusting your filter criteria.'
              }
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Application No
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Class
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trademark Applied For
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registry Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Year
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trademarkData.map((item, index) => (
                    <tr key={item.id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleAppNoClick(item.appno)}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                        >
                          {item.appno || '-'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span 
                          className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-white"
                          style={{ backgroundColor: getStatusColor(item.status) }}
                        >
                          {item.status || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.class || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {item.tmAppliedFor || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {item.buisnessName || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.registryLocation || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatYear(item.dateOfApp)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleCreateTask(item)}
                          className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                          title="Create Task"
                        >
                          <PlusCircleIcon className="h-4 w-4 mr-1" />
                          Create Task
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{((currentPage - 1) * limit) + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * limit, totalCount)}
                    </span>{' '}
                    of <span className="font-medium">{totalCount.toLocaleString()}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                    
                    {/* Page numbers */}
                    {(() => {
                      const pages = [];
                      const maxVisiblePages = 5;
                      
                      if (totalPages <= maxVisiblePages) {
                        // Show all pages if total is 5 or less
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        // Show smart pagination with ellipsis
                        if (currentPage <= 3) {
                          // Near the beginning: show 1, 2, 3, 4, 5, ..., last
                          for (let i = 1; i <= 5; i++) {
                            pages.push(i);
                          }
                          pages.push('...');
                          pages.push(totalPages);
                        } else if (currentPage >= totalPages - 2) {
                          // Near the end: show 1, ..., last-4, last-3, last-2, last-1, last
                          pages.push(1);
                          pages.push('...');
                          for (let i = totalPages - 4; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          // In the middle: show 1, ..., current-1, current, current+1, ..., last
                          pages.push(1);
                          pages.push('...');
                          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                            pages.push(i);
                          }
                          pages.push('...');
                          pages.push(totalPages);
                        }
                      }
                      
                      return pages.map((pageNum, index) => {
                        if (pageNum === '...') {
                          return (
                            <span
                              key={`ellipsis-${index}`}
                              className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                            >
                              ...
                            </span>
                          );
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              pageNum === currentPage
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      });
                    })()}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Task Creation Modal */}
      <TaskCreateModal
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
          setSelectedTrademark(null);
        }}
        onSuccess={handleTaskCreated}
        initialData={getTaskInitialData()}
      />
    </div>
  );
};

export default TrademarkPortfolio; 
